import { collection, deleteDoc, doc, getDocs, query, where } from "firebase/firestore";
import { FileText, Trash2, Scale } from "lucide-react";
import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import PageHeader from "../components/PageHeader";
import { useAuth } from "../context/AuthContext";
import { db } from "../firebase";

function Drafts() {
  const navigate = useNavigate();
  const { globalUser } = useAuth();
  const [drafts, setDrafts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    const fetchDrafts = async () => {
      if (!globalUser) return;
      setIsLoading(true);
      try {
        const q = query(
          collection(db, "drafts"),
          where("userId", "==", globalUser.uid)
        );
        const snapshot = await getDocs(q);
        const list = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
        // Sort newest first
        list.sort((a, b) => {
          const aTime = a.updatedAt?.seconds || a.createdAt?.seconds || 0;
          const bTime = b.updatedAt?.seconds || b.createdAt?.seconds || 0;
          return bTime - aTime;
        });
        setDrafts(list);
      } catch (err) {
        console.error("Error fetching drafts:", err);
        toast.error("Failed to load drafts.");
      } finally {
        setIsLoading(false);
      }
    };
    fetchDrafts();
  }, [globalUser]);

  const handleDelete = async (e, draftId) => {
    e.stopPropagation();
    setDeletingId(draftId);
    try {
      await deleteDoc(doc(db, "drafts", draftId));
      setDrafts((prev) => prev.filter((d) => d.id !== draftId));
      toast.success("Draft deleted.");
    } catch (err) {
      console.error("Error deleting draft:", err);
      toast.error("Failed to delete draft.");
    } finally {
      setDeletingId(null);
    }
  };

  const handleOpenDraft = (draft) => {
    const { id, userId, createdAt, updatedAt, ...draftData } = draft;
    navigate("/portal/AddCase", {
      state: {
        draftId: id,
        draftData,
      },
    });
  };

  const formatDate = (ts) => {
    if (!ts) return "—";
    const date = ts.seconds ? new Date(ts.seconds * 1000) : new Date(ts);
    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  return (
    <div className="min-h-screen bg-[#0c0a29] text-white relative">
      <Navbar />
      <div className="background-circle circle-3 z-0"></div>
      <div className="background-circle circle-4 z-0"></div>

      <main className="px-4 md:px-36 py-8 z-10 relative">
        <PageHeader
          title="Draft Cases"
          subtitle="Your saved case drafts"
          backTo="/portal/AddCase"
        />

        {isLoading ? (
          <div className="flex justify-center items-center py-24">
            <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-yellow-500"></div>
          </div>
        ) : drafts.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-24 text-gray-400">
            <FileText className="w-16 h-16 mb-4 opacity-40" />
            <p className="text-lg">No drafts saved yet.</p>
            <button
              onClick={() => navigate("/portal/AddCase")}
              className="mt-4 px-6 py-2 border border-yellow-500 text-yellow-500 rounded-lg hover:bg-yellow-500/10 transition-colors text-sm"
            >
              Add a Case
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {drafts.map((draft) => (
              <div
                key={draft.id}
                onClick={() => handleOpenDraft(draft)}
                className="relative bg-[#1e1b4b] rounded-lg border border-gray-700 hover:border-gray-500 cursor-pointer transition-all duration-200 group min-h-[200px]"
              >
                {/* Delete button */}
                <button
                  onClick={(e) => handleDelete(e, draft.id)}
                  disabled={deletingId === draft.id}
                  className="absolute top-3 right-3 text-gray-500 hover:text-red-400 transition-colors z-10"
                  title="Delete draft"
                >
                  {deletingId === draft.id ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-red-400"></div>
                  ) : (
                    <Trash2 className="w-4 h-4" />
                  )}
                </button>

                <div className="pt-8 pb-4 px-4 h-full">
                  <div className="flex h-full">
                    <div className="mr-4 flex-shrink-0 flex items-start">
                      <div className="w-12 h-12 sm:w-16 sm:h-16 flex items-center justify-center bg-[#2d2a5a] rounded-md">
                        <Scale size={32} color="#FFD700" strokeWidth={1.5} />
                      </div>
                    </div>
                    <div className="flex-grow overflow-hidden flex flex-col">
                      {draft.caseNumber && (
                        <p className="text-lg sm:text-xl font-bold text-white mb-2 truncate">Case No: {draft.caseNumber}</p>
                      )}
                      <h3 className="text-sm sm:text-base font-medium text-gray-200 mb-2 line-clamp-2 leading-snug">
                        {draft.petitionerName || "Unnamed"} vs {draft.respondentName || "Unnamed"}
                      </h3>
                      {draft.caseType && (
                        <p className="text-xs sm:text-sm text-gray-300 truncate">Type: {draft.caseType}</p>
                      )}
                      {draft.court && draft.court !== "Others" && (
                        <p className="text-xs sm:text-sm text-gray-300 truncate">Court: {draft.court}</p>
                      )}
                      {draft.status && (
                        <p
                          className={`text-xs sm:text-sm font-semibold truncate mt-auto ${
                            draft.status.toLowerCase() === "disposed" ? "text-red-400" : draft.status.toLowerCase() === "pending" ? "text-green-400" : "text-yellow-400"
                          }`}
                        >
                          Status: {draft.status.charAt(0).toUpperCase() + draft.status.slice(1).toLowerCase()}
                        </p>
                      )}
                      <div className="mt-2 pt-2 border-t border-gray-700 flex items-center justify-between">
                        <span className="text-xs text-gray-500">
                          Last saved: {formatDate(draft.updatedAt || draft.createdAt)}
                        </span>
                        <span className="text-xs text-yellow-500 opacity-0 group-hover:opacity-100 transition-opacity">
                          Open →
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

export default Drafts;
